import { useState, useEffect } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import axios from "axios";
import { Wifi, Loader2 } from "lucide-react";
import { API_URL } from "@/utils/apiConfig";
import WiFiPortalForm from "@/components/wifi/WiFiPortalForm";

const API = `${API_URL}/api/v1`;

// Only redirect to safe absolute URLs (prevents open-redirect to javascript: etc.)
function safeParsedUrl(raw) {
  if (!raw) return null;
  try {
    const u = new URL(raw);
    return (u.protocol === "https:" || u.protocol === "http:") ? u.href : null;
  } catch {
    return null;
  }
}

export default function WiFiPortalPage() {
  const { restaurantSlug } = useParams();
  const [searchParams] = useSearchParams();
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  // Captive portal params injected by the router in the redirect URL
  const clientMac = searchParams.get("client_mac") || searchParams.get("mac") || undefined;
  const clientIp = searchParams.get("client_ip") || searchParams.get("ip") || undefined;
  const redirectUrl = safeParsedUrl(
    searchParams.get("redirect_url") || searchParams.get("redirect") || searchParams.get("url")
  );

  useEffect(() => {
    if (!restaurantSlug) return;
    axios
      .get(`${API}/public/wifi-portal/${restaurantSlug}`)
      .then((r) => setConfig(r.data))
      .catch(() => setError("Portal Wi-Fi não encontrado ou inativo."))
      .finally(() => setLoading(false));
  }, [restaurantSlug]);

  // Merge captive-portal query params into every backend call
  const handleRegister = async (payload) => {
    const r = await axios.post(`${API}/public/wifi-portal/${restaurantSlug}/register`, {
      ...payload,
      mac_address: payload.mac_address || clientMac,
      device_ip: payload.device_ip || clientIp,
    });
    return r.data;
  };

  const handleAuthorize = async (payload) => {
    const r = await axios.post(`${API}/public/wifi-portal/${restaurantSlug}/authorize`, {
      ...payload,
      mac_address: payload.mac_address || clientMac,
      device_ip: payload.device_ip || clientIp,
    });
    return r.data;
  };

  // After successful auth: redirect to router's success URL if provided
  const handleSuccess = () => {
    if (!redirectUrl) return;
    setTimeout(() => { window.location.href = redirectUrl; }, 2500);
  };

  const primaryColor = config?.brand_primary_color || "#FF6B35";
  const bgColor = config?.brand_background_color || "#FFFFFF";

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
      </div>
    );
  }

  if (error || !config) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6">
        <div className="text-center space-y-3">
          <Wifi className="h-12 w-12 mx-auto text-gray-300" />
          <p className="text-gray-500">{error || "Portal não disponível."}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center p-4" style={{ backgroundColor: bgColor }}>
      <div className="w-full max-w-sm space-y-6">
        <div className="text-center space-y-3">
          {config.logo_url && (
            <img src={config.logo_url} alt="Logo" className="h-20 w-20 mx-auto object-contain rounded-xl" />
          )}
          {!config.logo_url && (
            <div className="h-16 w-16 mx-auto rounded-full flex items-center justify-center" style={{ backgroundColor: primaryColor }}>
              <Wifi className="h-8 w-8 text-white" />
            </div>
          )}
          <h1 className="text-2xl font-bold text-gray-900">{config.portal_title || "Wi-Fi Gratuito"}</h1>
          {config.portal_subtitle && <p className="text-sm text-gray-500">{config.portal_subtitle}</p>}
        </div>

        <div className="bg-white rounded-2xl shadow-lg p-6 border border-gray-100">
          <WiFiPortalForm
            config={config}
            onRegister={handleRegister}
            onAuthorize={handleAuthorize}
            onSuccess={handleSuccess}
            hasRedirect={!!redirectUrl}
          />
        </div>

        {config.show_powered_by_kaiso && (
          <p className="text-center text-xs text-gray-300">Powered by Kaiso System</p>
        )}
      </div>
    </div>
  );
}
